import React, { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box } from '@mui/material';
import LeadDialog from './LeadDialog';
import BiztantuLoader from './Loader';
import TextFiledWithLabel from './TextFieldWithLabel';
import SelectBoxWithLabel from './SelectBoxWithLable';
import AutoCompleteComponent from './AutoCompleteComponenet';
import WhoColumnTextfieds from './WhoColumnTextfieds';
import CustomizableFields from './CustomizableFields';
import { EntityNote } from './EntityNote';
import { StyledGridContainer, StyledLeadContainer, StyledSubHeader } from './Styled/index';
import { FieldWithOpenLink } from './FieldWithOpenLink';
import { privateRoutesPath } from '../Routes';
import { requestGetProductById } from '../Store/Product/action';
import { requestGetAllMerchant } from '../Store/Merchant/action';
import { getEntityColumns } from '../Utility/apiHelper';
import {
    ACTIVE_STATUS,
    ADDITIONAL_ATTRIBUTE_FIELDS,
    handleValidateCodevalue,
    PRODUCT_STATUS,
    PRODUCT_TYPE,
    PRODUCT_UOM,
    STATIC_ENTITY_CONFIG
} from '../Utility/constant';
import './ProductViewDialog.css';

const ProductViewDialog = ({ open, handleClose, productId }) => {
    const dispatch = useDispatch();

    const internationalise = useSelector(state => state?.internationalise?.currentHashMapData);
    const selectedProduct = useSelector(state => state?.product?.selectedProduct);
    const isProductLoading = useSelector(state => state?.product?.isProductLoading);
    const merchantData = useSelector(state => state?.merchant?.merchant || []);
    const specificRolePermission = useSelector(state => state?.owner?.rolebasedData);
    const allEntityData = useSelector((state) => state?.customization?.allEntityData || []);
    const lookupData = useSelector(state => state?.lookup?.lookupData);

    const productTypeData = useMemo(() => {
        return lookupData?.find((item) => item?.name === PRODUCT_TYPE)?.codes?.filter(item => item?.status === ACTIVE_STATUS)?.sort((a, b) => a?.sequence - b?.sequence);
    }, [lookupData]);

    const productStatusData = useMemo(() => {
        return lookupData?.find((item) => item?.name === PRODUCT_STATUS)?.codes?.filter(item => item?.status === ACTIVE_STATUS)?.sort((a, b) => a?.sequence - b?.sequence);
    }, [lookupData]);

    const productUOMData = useMemo(() => {
        return lookupData?.find((item) => item?.name === PRODUCT_UOM)?.codes?.filter(item => item?.status === ACTIVE_STATUS)?.sort((a, b) => a?.sequence - b?.sequence);
    }, [lookupData]);

    const productTextfieldsData = useMemo(() => {
        const data = getEntityColumns(allEntityData, "PRODUCT");
        return Object.keys(data).length > 0 ? data : STATIC_ENTITY_CONFIG?.PRODUCT;
    }, [allEntityData]);

    useEffect(() => {
        if (open && productId) {
            dispatch(requestGetProductById(productId));
            dispatch(requestGetAllMerchant());
        }
    }, [open, productId, dispatch]);

    const productDetail = useMemo(() => {
        if (!selectedProduct) return null;

        const filteredVendor = merchantData.find(
            item => item?.id === selectedProduct?.vendorId
        );

        return {
            ...selectedProduct,
            selectedVendor: filteredVendor || null
        };
    }, [selectedProduct, merchantData]);

    return (
        <LeadDialog
            open={open}
            handleClose={handleClose}
            title={`${handleValidateCodevalue(internationalise, 'PROD_EDIT_PRODUCT_DETAILS')} - ${productId}`}
            size="xl"
        >
            <BiztantuLoader open={isProductLoading} isChildren={true}>
                {productDetail && (
                    <Box className="pvd-dialog-content">
                        <StyledGridContainer>
                            <FieldWithOpenLink
                                id={productDetail?.id}
                                href={
                                    productDetail?.id
                                        ? `${privateRoutesPath.EDIT_PRODUCT}/${productDetail?.id}`
                                        : null
                                }
                                tooltip={handleValidateCodevalue(
                                    internationalise,
                                    "PROD_OPEN_PRODUCT"
                                )}
                            >
                                <TextFiledWithLabel
                                    id="id"
                                    name="id"
                                    value={productDetail?.id}
                                    label={handleValidateCodevalue(internationalise, 'PROD_EDIT_PRODUCT_ID')}
                                    disabled={true}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </FieldWithOpenLink>
                            <TextFiledWithLabel
                                disabled={true}
                                name="name"
                                label={handleValidateCodevalue(internationalise, 'PROD_EDIT_PRODUCT_NAME')}
                                value={productDetail?.name}
                                InputLabelProps={{ shrink: true }}
                            />
                            <SelectBoxWithLabel
                                ownerdropdown={false}
                                disabled={true}
                                name="status"
                                value={productDetail?.status || ""}
                                isStatic={false}
                                optionMenuItem={productStatusData}
                                label={handleValidateCodevalue(internationalise, 'PRODUCT_STATUS')}
                            />
                        </StyledGridContainer>

                        <StyledLeadContainer sx={{ width: "72% !important" }}>
                            <TextFiledWithLabel
                                sx={{ width: "100% !important" }}
                                disabled={true}
                                name="description"
                                label={handleValidateCodevalue(internationalise, 'PROD_EDIT_DESCRIPTION')}
                                value={productDetail?.description}
                                InputLabelProps={{ shrink: true }}
                            />
                        </StyledLeadContainer>

                        <StyledGridContainer>
                            <SelectBoxWithLabel
                                ownerdropdown={false}
                                disabled={true}
                                value={productDetail?.type || ""}
                                name="type"
                                label={handleValidateCodevalue(internationalise, 'PRODUCT_TYPE')}
                                optionMenuItem={productTypeData}
                                isStatic={false}
                            />
                            <SelectBoxWithLabel
                                ownerdropdown={false}
                                disabled={true}
                                name="unitOfMeasurement"
                                value={productDetail?.unitOfMeasurement || ""}
                                isStatic={false}
                                optionMenuItem={productUOMData}
                                label={handleValidateCodevalue(internationalise, 'PRODUCT_UOM')}
                            />
                            {(productTextfieldsData?.selectedVendor?.visibility && specificRolePermission?.isVendorView) && (
                                <AutoCompleteComponent
                                    isViewMode={true}
                                    optionSet={merchantData}
                                    value={productDetail?.selectedVendor || null}
                                    name="vendorId"
                                    placeholder={handleValidateCodevalue(internationalise, 'PROD_EDIT_VENDOR')}
                                    label={handleValidateCodevalue(internationalise, 'PROD_EDIT_VENDOR')}
                                />
                            )}
                        </StyledGridContainer>

                        <WhoColumnTextfieds
                            entityDetails={productDetail}
                        />

                        <StyledSubHeader>
                            {handleValidateCodevalue(internationalise, 'PROD_EDIT_PRICING')}
                        </StyledSubHeader>
                        <StyledGridContainer>
                            {productTextfieldsData?.mrp?.visibility && (
                                <TextFiledWithLabel
                                    disabled={true}
                                    id="mrp"
                                    type="number"
                                    name="mrp"
                                    value={productDetail?.mrp}
                                    label={handleValidateCodevalue(internationalise, 'PROD_EDIT_MRP')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            )}

                            <TextFiledWithLabel
                                disabled={true}
                                id="sellingPrice"
                                type="number"
                                name="sellingPrice"
                                value={productDetail?.sellingPrice}
                                label={handleValidateCodevalue(internationalise, 'PROD_EDIT_SP')}
                                InputLabelProps={{ shrink: true }}
                            />

                            {productTextfieldsData?.discount1Percentage?.visibility && (
                                <TextFiledWithLabel
                                    disabled={true}
                                    id="discount1Percentage"
                                    type="number"
                                    name="discount1Percentage"
                                    value={productDetail?.discount1Percentage}
                                    label={handleValidateCodevalue(internationalise, 'PROD_EDIT_DISCOUNT1_PERCENT')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            )}

                            {productTextfieldsData?.discount2Percentage?.visibility && (
                                <TextFiledWithLabel
                                    disabled={true}
                                    id="discount2Percentage"
                                    type="number"
                                    name="discount2Percentage"
                                    value={productDetail?.discount2Percentage}
                                    label={handleValidateCodevalue(internationalise, 'PROD_EDIT_DISCOUNT2_PERCENT')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            )}

                            {productTextfieldsData?.discount3Percentage?.visibility && (
                                <TextFiledWithLabel
                                    disabled={true}
                                    id="discount3Percentage"
                                    type="number"
                                    name="discount3Percentage"
                                    value={productDetail?.discount3Percentage}
                                    label={handleValidateCodevalue(internationalise, 'PROD_EDIT_DISCOUNT3_PERCENT')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            )}

                            <TextFiledWithLabel
                                disabled={true}
                                id="cgstPercentage"
                                type="number"
                                name="cgstPercentage"
                                value={productDetail?.cgstPercentage}
                                label={handleValidateCodevalue(internationalise, 'PROD_EDIT_CGST_PERCENT')}
                                InputLabelProps={{ shrink: true }}
                            />

                            <TextFiledWithLabel
                                disabled={true}
                                id="sgstPercentage"
                                type="number"
                                name="sgstPercentage"
                                value={productDetail?.sgstPercentage}
                                label={handleValidateCodevalue(internationalise, 'PROD_EDIT_SGST_PERCENT')}
                                InputLabelProps={{ shrink: true }}
                            />

                            {productTextfieldsData?.surchargePercentage?.visibility && (
                                <TextFiledWithLabel
                                    disabled={true}
                                    id="surchargePercentage"
                                    type="number"
                                    name="surchargePercentage"
                                    value={productDetail?.surchargePercentage}
                                    label={handleValidateCodevalue(internationalise, 'PROD_EDIT_SURCHARGE_PERCENT')}
                                    InputLabelProps={{ shrink: true }}
                                />
                            )}
                        </StyledGridContainer>

                        <CustomizableFields
                            CUSTOMIZABLE_FIELDS={ADDITIONAL_ATTRIBUTE_FIELDS}
                            entity="PRODUCT"
                            config={productTextfieldsData}
                            values={productDetail}
                            isViewMode={true}
                        />

                        <EntityNote
                            entityDetails={productDetail}
                            headingLabel={handleValidateCodevalue(internationalise, "COMMON_SPECIAL_NOTE")}
                            isViewMode={true}
                        />
                    </Box>
                )}
            </BiztantuLoader>
        </LeadDialog>
    );
};

export default ProductViewDialog;
